import { useState, useEffect, type ReactNode } from "react";
import { MsalProvider as MsalReactProvider } from "@azure/msal-react";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig } from "./msalConfig";

/**
 * MSAL instance — created outside component tree per Microsoft docs.
 * https://learn.microsoft.com/en-us/entra/identity-platform/tutorial-single-page-app-react-prepare-app
 */
const msalInstance = new PublicClientApplication(msalConfig);

export function MsalProvider({ children }: { children: ReactNode }) {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    msalInstance.initialize().then(() => {
      // Default to first account if no active account
      if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
        msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
      }

      // Listen for sign-in event and set active account
      msalInstance.addEventCallback((event) => {
        if (
          event.eventType === EventType.LOGIN_SUCCESS &&
          (event.payload as { account?: unknown })?.account
        ) {
          const account = (event.payload as { account: ReturnType<typeof msalInstance.getActiveAccount> }).account;
          msalInstance.setActiveAccount(account);
        }
      });

      setInitialized(true);
    });
  }, []);

  if (!initialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-[var(--muted-foreground)]">Cargando...</p>
      </div>
    );
  }

  return (
    <MsalReactProvider instance={msalInstance}>
      {children}
    </MsalReactProvider>
  );
}
